/**
 * Supply scarcity semantics — the status trio applied to serial-capped
 * finishes everywhere supply is shown (PackLineup, PackPurchasePanel,
 * WhatsInside, Collection tiles):
 *   plentiful → quiet ink-mute · low → warn yellow · sold out → danger red.
 * Display-only; thresholds mirror nothing on the backend.
 */
import type { AgentCardItem } from "@/lib/api";

export type SupplyStatus = "plentiful" | "low" | "soldout";

/** Low-supply when ≤20% of the cap remains… */
export const LOW_SUPPLY_RATIO = 0.2;
/** …or when only a hand-countable number of serials is left. */
export const LOW_SUPPLY_SERIALS = 3;

export function supplyStatus(remaining: number, cap: number): SupplyStatus {
  if (remaining <= 0) return "soldout";
  if (remaining <= LOW_SUPPLY_SERIALS || remaining <= cap * LOW_SUPPLY_RATIO) return "low";
  return "plentiful";
}

/** Text color per status — plentiful stays quiet; scarcity gets loud. */
export const SUPPLY_TEXT: Record<SupplyStatus, string> = {
  plentiful: "text-ink-mute",
  low: "text-yellow",
  soldout: "text-danger",
};

/**
 * Status for one catalog item (a single assistant × finish). Returns null
 * for open editions or when live supply data is absent (signed out /
 * backend unavailable) — callers keep their quiet default color.
 */
export function itemSupplyStatus(
  item?: Pick<AgentCardItem, "supplyCap" | "remainingSupply" | "mintedCount">,
): SupplyStatus | null {
  const cap = item?.supplyCap;
  if (cap == null) return null;
  const remaining = item?.remainingSupply ?? cap - (item?.mintedCount ?? 0);
  return supplyStatus(remaining, cap);
}
